import { pick } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { CALENDAR_SETTINGS } from '../../constants';
import {
    areBlocksOverlapping,
    checkCollision,
    isTimeWindowEqual,
    type TimeWindow,
} from '../../utils';
import type { ModalFormData } from '../types';
import { useAlarmScheduleField } from './useAlarmScheduleField';

export const minValue = 50;
export const maxValue = 140;

type ModalFormErrors = Record<string, string>;

export interface ModalFieldChangeEvent {
    target: {
        name: string;
        value: string | number;
        valueAsNumber?: number;
        type?: string;
    };
}

export function minMaxErrorMessage(): string {
    return interpolate(gettext('VALUE_SHOULD_BE_WITHIN_RANGE'), { minValue, maxValue }, true);
}

export function validateLevel(value: number | '' | null | undefined): boolean {
    // Can't be empty.
    if (value === '' || value === null || value === undefined) return false;
    // 0 is considered disabled.
    if (value === 0) return true;
    return value >= minValue && value <= maxValue;
}

function validateDayAndTime(formData: ModalFormData, dayStartOffset: number): ModalFormErrors {
    const newErrors: ModalFormErrors = {};
    const { endTime, startTime, startDay, endDay } = formData;

    // Validate that user doesn't wrap the calendar.
    if (
        areBlocksOverlapping(
            {
                startDay: startDay > endDay ? 1 + CALENDAR_SETTINGS.DAYS_IN_WEEK : 1,
                startTime: dayStartOffset * 100,
                endDay: startDay > endDay ? 1 + CALENDAR_SETTINGS.DAYS_IN_WEEK : 1,
                endTime: dayStartOffset * 100,
            },
            {
                startDay,
                startTime,
                endDay: startDay > endDay ? endDay + CALENDAR_SETTINGS.DAYS_IN_WEEK : endDay,
                endTime,
            }
        )
    ) {
        newErrors.startDay = gettext('CANT_WRAP_CALENDAR');
        newErrors.endDay = gettext('CANT_WRAP_CALENDAR');
    }

    if (endTime <= startTime && startDay === endDay) {
        newErrors.endTime = gettext('START_BEFORE_END_TIME');
        newErrors.startTime = gettext('END_BEFORE_START_ERROR');
    }

    return newErrors;
}

interface UseModalFormArgs {
    ldenSchedule: ModalFormData;
    customTemplate?: TimeWindow[] | null;
    onSave: (ldenSchedule: ModalFormData, blockData: TimeWindow) => void;
    onClose: () => void;
    dayStartOffset: number;
}

interface UseModalFormReturn {
    formData: ModalFormData;
    errors: ModalFormErrors;
    handleChange: (event: ModalFieldChangeEvent) => void;
    handleDayChange: (selectedDay: number, isStartTime: boolean) => void;
    validate: () => boolean;
    handleSave: () => void;
    handleDeleteSchedule: (ldenSchedule: ModalFormData) => void;
    alarmSettings: ReturnType<typeof useAlarmScheduleField>['settings'];
}

export function useModalForm({
    ldenSchedule,
    customTemplate,
    onSave,
    onClose,
    dayStartOffset,
}: UseModalFormArgs): UseModalFormReturn {
    const {
        getAlarmSchedule,
        handleDelete: handleDeleteSchedule,
        settings,
        handleSaveModalForm,
    } = useAlarmScheduleField();

    const initializeFormData = useCallback(() => {
        const alarmSchedule = getAlarmSchedule(ldenSchedule);
        return {
            ...ldenSchedule,
            ...alarmSchedule,
        } as ModalFormData;
    }, [ldenSchedule, getAlarmSchedule]);

    const [formData, setFormData] = useState<ModalFormData>(initializeFormData);
    const [errors, setErrors] = useState<ModalFormErrors>({});

    useEffect(() => {
        if (ldenSchedule) {
            setFormData(initializeFormData());
            setErrors({});
        }
    }, [ldenSchedule, initializeFormData]);

    // Run validation whenever form data changes.
    useEffect(() => {
        if (formData.startDay !== undefined && formData.endDay !== undefined) {
            const newErrors = validateDayAndTime(formData, dayStartOffset);
            setErrors(newErrors);
        }
    }, [
        formData.startDay,
        formData.endDay,
        formData.startTime,
        formData.endTime,
        formData,
        dayStartOffset,
    ]);

    const handleChange = useCallback(
        (event: ModalFieldChangeEvent) => {
            const { name, value, valueAsNumber, type } = event.target;
            let newValue: unknown;

            if (type === 'number') {
                // Empty string if field is cleared, otherwise valueAsNumber.
                newValue = value === '' ? '' : valueAsNumber;
            } else {
                newValue = value;
            }

            setFormData((prev) => ({
                ...prev,
                [name]: newValue,
            }));
        },
        [setFormData]
    );

    const handleDayChange = useCallback((selectedDay: number, isStartTime: boolean) => {
        setFormData((previousData) => ({
            ...previousData,
            [isStartTime ? 'startDay' : 'endDay']: selectedDay,
        }));
    }, []);

    const validate = useCallback(() => {
        const newErrors: ModalFormErrors = {};
        if (!formData.correction && formData.correction !== 0) {
            newErrors.correction = gettext('FIELD_REQUIRED_VALIDATION');
        }
        (['warningLevel', 'alarmLevel', 'leqLevel', 'lmaxLevel'] as const).forEach((key) => {
            if (!validateLevel(formData[key] as number | '')) {
                newErrors[key] = minMaxErrorMessage();
            }
        });

        // Validate day and time ranges.
        const dayTimeErrors = validateDayAndTime(formData, dayStartOffset);
        Object.assign(newErrors, dayTimeErrors);

        const template = customTemplate ?? [];
        const customTemplateWithoutUs = template.filter(
            (schedule) => !isTimeWindowEqual(schedule, ldenSchedule)
        );
        if (checkCollision(formData, customTemplateWithoutUs)) {
            // We know this block overlaps, but not if it's start or end.
            // So just show on both and let the user figure it out.
            newErrors.endTime = gettext('BLOCK_OVERLAP');
            newErrors.startTime = gettext('BLOCK_OVERLAP');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [customTemplate, dayStartOffset, formData, ldenSchedule]);

    useEffect(() => {
        validate();
    }, [formData, validate]);

    const handleSave = useCallback(() => {
        if (validate()) {
            const blockData = pick(formData, [
                'name',
                'correction',
                'alarmLevel',
                'warningLevel',
                'startDay',
                'endDay',
                'startTime',
                'endTime',
            ]) as TimeWindow;

            handleSaveModalForm(ldenSchedule, formData);

            onSave(ldenSchedule, blockData);
            onClose();
        }
    }, [validate, formData, handleSaveModalForm, ldenSchedule, onSave, onClose]);

    return {
        formData,
        errors,
        handleChange,
        handleDayChange,
        validate,
        handleSave,
        handleDeleteSchedule,
        alarmSettings: settings,
    };
}
